import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';

import { ModalComponent } from '../../modal/modal.component';
import { StatesPluginService } from './states-plugin.service';
import { BaseModalPageComponent } from '../../base-modal.component';
import { ConfirmComponent } from '../../modal/confirm.component';
import { Impact } from './impact.model';
import * as _ from 'lodash';
import { LogService } from '../../shared/services/log.service';

@Component({
  selector: 'states-edit',
  templateUrl: './states-edit.component.html',
  standalone: false
})
export class StatesEditPageComponent extends BaseModalPageComponent implements OnInit {
  inspectionId: string;
  pointId: string;
  pluginId: string;
  public plugin: any;
  public states: any;
  public actions: any;
  public impactSelectorValues: any;
  public impact = new Impact();
  public processing = false;
  public criticaly: boolean;
  public showCriticaly = false;

  public priorities: any;
  public priority: any;

  @ViewChild('statesModal', { static: true })
  stateModal: ModalComponent;
  @ViewChild('actionModal', { static: true })
  actionModal: ModalComponent;
  editForm: UntypedFormGroup;
  @ViewChild('menuModal', { static: true })
  menuModal: ModalComponent;
  @ViewChild('deleteConfirm', { static: true })
  deleteConfirm: ConfirmComponent;
  @ViewChild('pictureRequiredConfirm', { static: true })
  pictureRequiredConfirm: ConfirmComponent;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private service: StatesPluginService,
    private logService: LogService,
    location: Location,
    private cdf: ChangeDetectorRef
  ) {
    super(location);
  }

  initRoute(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      const id = params['inspectionId'];
      if (id == null) this.goToBack();

      this.inspectionId = id;

      const pointId = params['pointId'];
      if (pointId == null) this.goToBack();

      this.pointId = pointId;

      const pluginId = params['pluginId'];
      if (pluginId == null) this.goToBack();

      this.pluginId = pluginId;

      this.initData();
    });
  }

  save() {
    this.processing = true;
    this.service
      .updateState(
        this.inspectionId,
        this.pointId,
        this.pluginId,
        this.editForm.controls['states'].value,
        this.impact,
        this.editForm.controls['action'].value,
        this.criticaly,
        this.priority
      )
      .subscribe(
        (res) => {
          this.goToBack();
          this.processing = false;
        },
        (error) => {
          this.processing = false;
          window.alert('An error prevented the plugin update. Please retry later or contact support for assistance.');
          const data = {
            inspectionId: this.inspectionId,
            pointId: this.pointId,
            pluginId: this.pluginId,
            formStatesValue: this.editForm.controls['states'].value,
            impact: this.impact,
            formActionValue: this.editForm.controls['action'].value,
            criticaly: this.criticaly,
            priority: this.priority
          };
          this.logService.logError({ source: 'states-edit.component', action: 'updateState', error, data });
        }
      );
  }

  cancel() {
    this.goToBack();
  }

  ngOnInit() {
    this.initRoute();
  }

  initData() {
    this.states = this.service.getStates();
    this.actions = _.orderBy(this.service.getActions(), ['label']);
    this.impactSelectorValues = this.service.getImpacts();

    this.priorities = this.service.getPriorities();

    this.service.getPlugin(this.inspectionId, this.pluginId).subscribe((data) => {
      this.plugin = data;
      this.impact.initImpactValue(data.config.Impact);
      this.showCriticaly = data.config.Criticaly;
      this.criticaly = data.config.Criticaly;
      this.priority = data.config.Priority;
      this.initForm();
    });
  }

  public openStatesSelector(): void {
    this.openSelector(this.stateModal, this.states, this.editForm.controls['states'].value);
  }

  public openActionSelector(): void {
    this.openSelector(this.actionModal, this.actions, this.editForm.controls['action'].value);
  }

  public selectStatesMenu(item: any) {
    const statesControl = this.editForm.controls['states'];
    const selectedIndex = statesControl.value === item.selectedIndex ? -1 : item.selectedIndex;
    statesControl.setValue(selectedIndex);

    const actionControl = this.editForm.controls['action'];

    if (selectedIndex !== 0 && selectedIndex !== -1) {
      actionControl.setValidators([Validators.required]);
    } else {
      actionControl.setValidators(null);
    }
    this.priority = -1;
    this.reset();

    actionControl.markAsTouched();
    actionControl.updateValueAndValidity();
    this.cdf.detectChanges();
  }

  reset() {
    this.selectActionMenu({ selectedIndex: -1 });
    this.criticaly = false;
  }

  public onStatesMenuClose() {
    this.editForm.controls['states'].markAsTouched();
  }

  public onImpactMenuClose() {
    this.editForm.controls['impactSelectedValue'].markAsTouched();
  }

  public selectActionMenu(item: any) {
    const selectedIndex = this.editForm.controls['action'].value === item.selectedIndex ? -1 : item.selectedIndex;
    this.editForm.controls['action'].setValue(selectedIndex);
  }

  public onActionMenuClose() {
    this.editForm.controls['action'].markAsTouched();
  }

  public changeImpact(propertyName: string) {
    this.impact.changeImpact(propertyName);
    this.criticaly = this.impact.getImpactValue('Safety');
  }

  public isImpactValid() {
    const values = [1, 2, 3, 4];
    const value = this.editForm.controls['states'].value;
    return !(values.includes(value) && !this.impact.isAnyTrue());
  }

  public isActionValid() {
    const values = [1, 2, 3, 4];
    const value = this.editForm.controls['states'].value;
    return !(values.includes(value) && this.editForm.controls['action'].value === -1);
  }

  public isStatusValid() {
    return this.editForm.controls['states'].value !== -1;
  }

  get isStatusOk() {
    return this.editForm.controls['states'].value === 0;
  }

  isPrioValid() {
    return this.isStatusOk || (this.priority !== undefined && this.priority != -1);
  }

  initForm() {
    this.editForm = new UntypedFormGroup({
      states: new UntypedFormControl(
        this.service.getStates().some((s) => s.value === this.plugin.config.States) ? this.plugin.config.States : -1,
        Validators.required
      ),
      action: new UntypedFormControl(
        this.service.getActions().some((a) => a.value === this.plugin.config.Action) ? this.plugin.config.Action : -1,
        []
      )
    });
  }

  public selectMenu(item: any) {
    switch (item) {
      case 1:
        this.openDeleteConfirm();
        break;
      case 10:
        this.gotoEditJsonPage();
        break;
      case 11:
        this.gotoEditPage();
        break;
      default:
    }
  }

  gotoEditJsonPage() {
    return this.router.navigate([this.inspectionsRoute, this.inspectionId, 'points', this.pointId, 'plugins', this.pluginId, 'editjson']);
  }

  gotoEditPage() {
    return this.router.navigate([this.inspectionsRoute, this.inspectionId, 'points', this.pointId, 'plugins', this.pluginId, 'edit']);
  }

  openDeleteConfirm() {
    this.deleteConfirm.open();
  }

  openPictureRequired() {
    if (
      this.editForm.controls['states'].value <= 0 ||
      this.plugin.point.plugins.some((p) => p.pluginType === 'files' && p.config.Assets?.length > 0)
    ) {
      this.save();
      return;
    }
    this.pictureRequiredConfirm.open();
  }

  deletePlugin() {
    this.service.deletePlugin(this.inspectionId, this.pointId, this.pluginId).subscribe((success: boolean) => {
      if (success) {
        this.goToBack();
      }
    });
  }

  isPriority(prio: any): boolean {
    return this.priority === prio.value;
  }

  changePriority(prio: any) {
    this.priority = prio.value;
  }
}
